import { AppUpdate, AppUpdateAvailability } from "@capawesome/capacitor-app-update";
import { Dialog } from "@capacitor/dialog";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { STORAGE_KEYS } from "./storage/storage";
import { appUpdateLog } from "./appUpdateLogger";

const POSTPONE_DURATION_MS = 12 * 60 * 60 * 1000; // 12 godzin

/**
 * Sprawdza czy użytkownik odłożył aktualizację na później
 */
async function isUpdatePostponed(): Promise<boolean> {
	if (!Capacitor.isNativePlatform()) {
		appUpdateLog.info("PostponeCheck", "Skipping postpone check on web platform");
		return true; // Nie pokazuj na web
	}

	try {
		const { value } = await Preferences.get({
			key: STORAGE_KEYS.APP_UPDATE_POSTPONE_UNTIL,
		});
		if (!value) {
			appUpdateLog.info("PostponeCheck", "No postpone value found - update not postponed");
			return false;
		}

		const postponeUntil = parseInt(value, 10);
		const isPostponed = Date.now() < postponeUntil;
		const postponeUntilDate = new Date(postponeUntil);
		
		if (isPostponed) {
			appUpdateLog.info(
				"PostponeCheck",
				`Update is postponed until ${postponeUntilDate.toLocaleString()}`,
				{
					postponeUntil,
					postponeUntilDate: postponeUntilDate.toISOString(),
					timeRemaining: postponeUntil - Date.now(),
					timeRemainingHours: ((postponeUntil - Date.now()) / (1000 * 60 * 60)).toFixed(2),
				}
			);
		} else {
			appUpdateLog.info(
				"PostponeCheck",
				`Postpone period expired at ${postponeUntilDate.toLocaleString()}`,
				{ postponeUntil, postponeUntilDate: postponeUntilDate.toISOString() }
			);
		}
		
		return isPostponed;
	} catch (error) {
		appUpdateLog.error(
			"PostponeCheck",
			"Error checking postpone status",
			{ error: error instanceof Error ? error.message : String(error) }
		);
		return false;
	}
}

/**
 * Zapisuje decyzję użytkownika o odłożeniu aktualizacji
 */
async function postponeUpdate(): Promise<void> {
	const postponeUntil = Date.now() + POSTPONE_DURATION_MS;
	const postponeUntilDate = new Date(postponeUntil);
	
	appUpdateLog.info(
		"PostponeUpdate",
		`User postponed update until ${postponeUntilDate.toLocaleString()}`,
		{
			postponeUntil,
			postponeUntilDate: postponeUntilDate.toISOString(),
			durationHours: POSTPONE_DURATION_MS / (1000 * 60 * 60),
		}
	);
	
	await Preferences.set({
		key: STORAGE_KEYS.APP_UPDATE_POSTPONE_UNTIL,
		value: postponeUntil.toString(),
	});
}

/**
 * Obsługuje proces aktualizacji w zależności od platformy
 */
async function handleUpdate(platform: string): Promise<void> {
	appUpdateLog.info("HandleUpdate", `Starting update process for platform: ${platform}`);
	
	if (platform === "android") {
		try {
			appUpdateLog.info("HandleUpdate", "Attempting immediate update (Android In-App Update)");
			// Spróbuj natychmiastowej aktualizacji (Android In-App Update)
			await AppUpdate.performImmediateUpdate();
			appUpdateLog.success("HandleUpdate", "Immediate update initiated successfully");
		} catch (error) {
			appUpdateLog.warning(
				"HandleUpdate",
				"Immediate update failed, falling back to Play Store",
				{ error: error instanceof Error ? error.message : String(error) }
			);
			// Fallback: otwórz Play Store
			await AppUpdate.openAppStore();
			appUpdateLog.info("HandleUpdate", "Opened Play Store as fallback");
		}
	} else if (platform === "ios") {
		appUpdateLog.info("HandleUpdate", "Opening App Store (iOS requires App Store redirect)");
		// iOS zawsze wymaga przekierowania do App Store
		await AppUpdate.openAppStore();
		appUpdateLog.success("HandleUpdate", "App Store opened successfully");
	}
}

/**
 * Sprawdza dostępność aktualizacji i wyświetla dialog jeśli potrzebne
 */
export async function checkForAppUpdate(): Promise<void> {
	appUpdateLog.info("CheckUpdate", "Starting app update check");
	
	// Tylko dla iOS i Android
	if (!Capacitor.isNativePlatform()) {
		appUpdateLog.info("CheckUpdate", "Skipping update check - not a native platform");
		return;
	}

	const platform = Capacitor.getPlatform();
	appUpdateLog.info("CheckUpdate", `Platform detected: ${platform}`);
	
	if (platform !== "ios" && platform !== "android") {
		appUpdateLog.info("CheckUpdate", `Skipping update check - unsupported platform: ${platform}`);
		return;
	}

	// Sprawdź czy użytkownik odłożył aktualizację
	const isPostponed = await isUpdatePostponed();
	if (isPostponed) {
		appUpdateLog.info("CheckUpdate", "Update check skipped - postponed by user");
		return;
	}

	try {
		appUpdateLog.info("CheckUpdate", "Fetching app update info from AppUpdate API");
		
		// Sprawdź dostępność aktualizacji
		// iOS parametr country
		// const updateInfoOptions = platform === "ios" 
		// 	? { country: "PL" } // Kod kraju dla Polski (można zmienić na inny)
		// 	: undefined;
		
		// const result = await AppUpdate.getAppUpdateInfo(updateInfoOptions);
		const result = await AppUpdate.getAppUpdateInfo();
		
		appUpdateLog.info(
			"CheckUpdate",
			"App update info retrieved",
			{
				updateAvailability: result.updateAvailability,
				availableVersion: result.availableVersionName,
				currentVersion: result.currentVersionName,
				immediateUpdateAllowed: result.immediateUpdateAllowed,
				flexibleUpdateAllowed: result.flexibleUpdateAllowed,
				fullResult: result,
			}
		);

		if (result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
			appUpdateLog.info(
				"CheckUpdate",
				`No update available. Status: ${result.updateAvailability}`,
				{
					updateAvailability: result.updateAvailability,
					currentVersion: result.currentVersionName,
					availableVersion: result.availableVersionName,
				}
			);
			return;
		}

		appUpdateLog.success(
			"CheckUpdate",
			"Update available! Showing dialog to user",
			{
				currentVersion: result.currentVersionName,
				availableVersion: result.availableVersionName,
			}
		);

		// Wyświetl dialog z opcjami
		appUpdateLog.info("CheckUpdate", "Displaying update dialog to user");
		const confirmResult = await Dialog.confirm({
			title: "Dostępna aktualizacja",
			message:
				"Dostępna jest nowa wersja aplikacji. Czy chcesz ją teraz zaktualizować?",
			okButtonTitle: "AKTUALIZUJ",
			cancelButtonTitle: "PÓŹNIEJ",
		});

		if (confirmResult.value) {
			// Użytkownik wybrał AKTUALIZUJ
			appUpdateLog.success("CheckUpdate", "User chose to update now");
			await handleUpdate(platform);
		} else {
			// Użytkownik wybrał PÓŹNIEJ
			appUpdateLog.info("CheckUpdate", "User chose to postpone update");
			await postponeUpdate();
		}
	} catch (error) {
		appUpdateLog.error(
			"CheckUpdate",
			"Error checking for update",
			{
				error: error instanceof Error ? error.message : String(error),
				errorStack: error instanceof Error ? error.stack : undefined,
				errorObject: error,
			}
		);
		// Nie pokazuj błędu użytkownikowi - ciche logowanie
	}
}

if (import.meta.env.MODE === "development" || import.meta.env.MODE === "staging") {
  // @ts-expect-error - tylko dla testowania
  if (typeof window !== "undefined") {
    // @ts-expect-error
    window.testAppUpdate = checkForAppUpdate;
  }
}

